import React from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { getTopicLabels, TopicId } from "../../../../constants/topics";
import { MAX_CAPACITY } from "../../../../constants/common";
import { formatRoomStart, getStartsInLabel } from "../../../../utils/timeUtils";
import { Room } from "../../../../types/types";

type MeetingOptionProps = {
  room: Room;
  isSelected: boolean;
  onSelect: () => void;
  participantsCount?: number;
  isDisabled?: boolean;
  showFullVisual?: boolean;
  showDisabledState?: boolean;
  disabledActionLabel?: string;
};

const MeetingOption: React.FC<MeetingOptionProps> = ({
  room,
  isSelected,
  onSelect,
  participantsCount = 0,
  isDisabled = false,
  showFullVisual = false,
  showDisabledState = true,
  disabledActionLabel,
}) => {
  const channelId = room.id;
  const roomName = room.name || "Untitled room";
  const roomStartAt = room.startAt;
  const roomTopics = room.topics;

  const topicsArray = roomTopics?.length
    ? getTopicLabels(roomTopics as TopicId[])
    : [];

  const formattedTime = formatRoomStart(roomStartAt);
  const relativeTime = getStartsInLabel(roomStartAt);

  const shouldShowDisabledState = showDisabledState && isDisabled;
  const shouldShowDisabledVisual = showFullVisual || shouldShowDisabledState;
  const shouldShowFullVisual =
    showFullVisual || (shouldShowDisabledState && !disabledActionLabel);
  const roomMaxCapacity = room.maxParticipants ?? MAX_CAPACITY;
  const capacityText = participantsCount
    ? `${Math.min(participantsCount, roomMaxCapacity)}/${roomMaxCapacity}`
    : `1/${roomMaxCapacity}`;
  const actionLabel =
    isDisabled && disabledActionLabel
      ? disabledActionLabel
      : shouldShowFullVisual
        ? "FULL"
        : "JOIN CONVERSATION";

  return (
    <TouchableOpacity
      onPress={onSelect}
      style={[
        styles.card,
        isSelected && styles.cardSelected,
        shouldShowDisabledState && styles.cardDisabled,
      ]}
      accessibilityRole="button"
      accessibilityState={{
        selected: isSelected,
        disabled: shouldShowDisabledState,
      }}
      testID={`meeting-option-${channelId}`}
      activeOpacity={0.9}
      disabled={shouldShowDisabledState}
    >
      <View
        style={[
          styles.content,
          shouldShowDisabledState && styles.contentDisabled,
        ]}
      >
        <View style={styles.header}>
          <Text style={styles.title}>{roomName}</Text>

          <Text style={styles.meta}>
            {[relativeTime, `${capacityText} people`, formattedTime]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </View>

        <View style={styles.blueDivider} />

        <View style={styles.tagsWrap}>
          {topicsArray.slice(0, 6).map((tag, idx) => (
            <View key={`${channelId}-tag-${idx}`} style={styles.tagPill}>
              <Text style={styles.tagText} numberOfLines={1}>
                {tag}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.joinButton,
            shouldShowDisabledVisual && styles.joinButtonDisabled,
          ]}
        >
          <Text
            style={[
              styles.joinButtonText,
              shouldShowDisabledVisual && styles.joinButtonTextDisabled,
            ]}
          >
            {actionLabel}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default MeetingOption;

const styles = StyleSheet.create({
  card: {
    flex: 1,
    width: "100%",
    maxWidth: 469,
    minHeight: 367,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "#E2E5EA",
    backgroundColor: "#FFFFFF",
    marginVertical: 10,
    overflow: "hidden",
  },
  cardSelected: {
    borderColor: "#3A6FB1",
  },
  cardDisabled: {
    backgroundColor: "#EEF0F3",
    borderColor: "#D0D4DA",
  },

  // Content Container (as in screenshot)
  content: {
    backgroundColor: "#FFFFFF",
    paddingTop: 40,
    paddingBottom: 16,
    paddingHorizontal: 40,
  },
  contentDisabled: {
    backgroundColor: "#EEF0F3",
  },

  header: {
    marginBottom: 16,
  },
  title: {
    fontSize: 32,
    lineHeight: 38,
    fontWeight: "700",
    fontFamily: "Lexend",
    color: "#2B2B2F",
  },
  meta: {
    marginTop: 10,
    fontSize: 16,
    lineHeight: 22,
    fontWeight: "400",
    fontFamily: "Lexend",
    color: "#6E6E6E",
  },

  blueDivider: {
    height: 3,
    backgroundColor: "#3A6FB1",
    borderRadius: 100,
    marginBottom: 16,
  },

  tagsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 16,
  },
  tagPill: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#D8DBE1",
    backgroundColor: "#F6F7F9",
  },
  tagText: {
    fontSize: 14,
    lineHeight: 14,
    fontWeight: "400",
    fontFamily: "Lexend",
    color: "#6E6E6E",
  },

  joinButton: {
    height: 62,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E5EA",
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  joinButtonDisabled: {
    backgroundColor: "#F6F7F9",
  },
  joinButtonText: {
    fontSize: 14,
    lineHeight: 14,
    fontWeight: "700",
    fontFamily: "Lexend",
    letterSpacing: 0.6,
    color: "#3A6FB1",
  },
  joinButtonTextDisabled: {
    color: "#6E6E6E",
  },
});
